import { effect, signal } from '@preact/signals';
import { clamp, round, sum, throttle } from 'es-toolkit';
import { debounce } from 'es-toolkit/compat';
import { RefCallback } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import waitForTimers from 'wait-for-timers';
import { Episode, EpisodeCompoundId } from '../models/podcasts';
import { bumpJobPriority } from './api';
import debugLog from './debug';
import { reportSentryError } from './error-handling';
import { exposeOnWindow, isPlayable } from './helpers';
import { topNavHeight } from './ui-measurements';

const log = (...args: any[]) => {
  debugLog({ logName: 'priority', logColor: 'gray' }, ...args);
};

// Keep track of the episode priority bumps we've already synced to the API this session
// Never sync more than the max value per episode
const syncedPriorities: Record<EpisodeCompoundId, number> = {};
const syncEpisodePriorityBumps = debounce(
  (trackedValues: Record<EpisodeCompoundId, number>) => {
    const valuesToSync: Record<EpisodeCompoundId, number> = {};
    const maxPriorityBump = 5;
    const minPriorityBump = -10;
    for (const _key in trackedValues) {
      const episodeId = _key as EpisodeCompoundId;
      const trackedValue = trackedValues[episodeId];
      const syncedValue = syncedPriorities[episodeId] || 0;
      const difference = trackedValue - syncedValue;
      // API only accepts integers between -10 and 5
      const bump = Math.trunc(clamp(difference, minPriorityBump, maxPriorityBump));
      if (bump) {
        valuesToSync[episodeId] = bump;
        syncedPriorities[episodeId] = (syncedPriorities[episodeId] || 0) + bump;
      }
    }
    if (Object.keys(valuesToSync).length) {
      log('Syncing updated priorities', valuesToSync);
      bumpJobPriority(valuesToSync);
    }
  },
  1000,
  {
    maxWait: 5 * 1000,
  }
);

// Increment priority of episodes as they're visible in the UI.
// A signal is used here so we can display it in the <EpisodeItem /> component for debugging
export const trackedPriorities = signal<Record<EpisodeCompoundId, number>>({});
effect(() => {
  syncEpisodePriorityBumps(trackedPriorities.value);
});

function noop() {}

export function useBumpEpisodePriorityWhenInView() {
  const [divToEpisodeMap] = useState(() => new WeakMap<Element, Episode>());
  const [observerState] = useState(() => lazyObserver(divToEpisodeMap));
  useEffect(() => observerState.cleanup, []); // Cleanup on unmount

  const observeRef: (episode: Episode) => RefCallback<HTMLDivElement> = useCallback(
    (episode: Episode) => {
      // Ad detection completed. No need to bump priority or even observe this element
      if (episode.isSaved && episode.ads) return noop;

      // Episode has .url property set so ad-skipping is disabled
      if (episode.url) return noop;

      // Episode audio cannot be played (e.g. it's a video)
      if (!isPlayable(episode)) return noop;

      return (contentDiv: HTMLDivElement | null) => {
        // No element to observe (should never happen)
        if (!contentDiv) return;

        // Observe element and bump priority when it's in the viewport
        observerState.observer.observe(contentDiv);
        divToEpisodeMap.set(contentDiv, episode);
        // log('observing', episode.title);

        // Cleanup
        return () => {
          // log('Unobserving', episode.title);
          observerState.observer.unobserve(contentDiv);
          divToEpisodeMap.delete(contentDiv);
        };
      };
    },
    // Technically we care about observerState.observer, but this allows us to not initialize it until it's needed
    [observerState]
  );

  return observeRef;
}

// Setup an intersection observer (lazily) to bump episodes' priority when they're in the viewport
function lazyObserver(divToEpisodeMap: WeakMap<Element, Episode>) {
  if (typeof window === 'undefined')
    return { cleanup: noop, observer: { observe: noop, unobserve: noop } }; // For SSR

  const currentEntryMap = new Map<EpisodeCompoundId, IntersectionObserverEntry>();
  const topNavOffsetPx = topNavHeight;

  // Used to determine how long since user last scrolled (to avoid bumping priority when user is idle)
  let lastScrolledTimestamp = performance.now();

  // Set lazily when needed
  let listHeight = NaN;

  // If set to undefined, no timer is running
  let cancel: undefined | (() => void);

  // If currently paused
  let pausedReason: string | undefined;

  // Queue a new episode scoring run
  function queue() {
    // Prevent queueing if page is hidden or user hasn't scrolled recently
    if (document.visibilityState === 'hidden') {
      pausedReason = 'hidden';
      log('Paused priority bumping because page is hidden');
      return;
    }
    if (performance.now() - lastScrolledTimestamp > 5 * 1000) {
      pausedReason = 'no-recent-scroll';
      log("Paused priority bumping because user hasn't scrolled in 5s");
      return;
    }
    if (cancel) {
      reportSentryError(new Error('Already priority bumping. Skipping...')); // Should never happen
      return;
    }
    pausedReason = undefined;

    const queuedSince = performance.now();
    cancel = waitForTimers([300, 'ric'], () => {
      cancel = undefined;
      const now = performance.now();
      const queuedForMs = now - queuedSince;
      const bottomThirdCutoff =
        (listHeight - topNavOffsetPx) * (2 / 3) + topNavOffsetPx;

      const episodeIds = Array.from(currentEntryMap.keys());
      if (episodeIds.length === 0) {
        log('No episodes visible in viewport. Skipping...');
        return;
      }

      const entryScores: Record<EpisodeCompoundId, number> = {};

      // Nothing scientific, just best guess when testing. Ideally priority bump of 1 happens fairly
      // quickly but 2 or 3 takes a good while--means they're actually reading description.
      const scoreMultiplier = 1 / 300;
      const decayMultiplier = 1 / 5; // of average score

      for (const episodeId of currentEntryMap.keys()) {
        const entry = currentEntryMap.get(episodeId)!;

        // Ignore episodes that weren't visible for most of this priority bumping interval
        if (now - entry.time < queuedForMs / 2) continue;

        const timeViewedMs = Math.round(Math.min(queuedForMs, now - entry.time));
        const rect = entry.target.getBoundingClientRect();
        const percentageOfListHeight = rect.height / listHeight;
        const nearBottomPenalty =
          rect.bottom < bottomThirdCutoff
            ? 1
            : rect.top < bottomThirdCutoff
              ? 0.75
              : 0.5;

        const score =
          timeViewedMs * percentageOfListHeight * nearBottomPenalty * scoreMultiplier;

        entryScores[episodeId] = score;
      }

      const scoresList = Object.values(entryScores);
      if (scoresList.length === 0) {
        // This will only happen if there is an episode but it hasn't been in viewport long enough. Try again...
        log('No episodes in viewport long enough. Retrying...');
        return queue();
      }

      const averageScore = sum(scoresList) / scoresList.length;
      const decay = averageScore * decayMultiplier;

      // Mutate cloned signal and then set it back
      const mutable = { ...trackedPriorities.peek() };
      for (const _id in entryScores) {
        const episodeId = _id as EpisodeCompoundId;
        // Bump priority for the episode in viewport
        mutable[episodeId] = round(
          (mutable[episodeId] || 0) + entryScores[episodeId],
          3
        );
      }
      for (const _id in mutable) {
        const episodeId = _id as EpisodeCompoundId;
        if (!entryScores[episodeId]) {
          // Decay priority for the episode not in viewport
          mutable[episodeId] = round(mutable[episodeId] - decay, 3);
        }
      }

      trackedPriorities.value = { ...mutable };
      // log({
      //   newScores: scoresList.map((score) => round(score, 3)),
      //   decay: round(decay, 3),
      //   // totals: mutable,
      // });

      queue();
    });
  }

  let _observer: IntersectionObserver;
  const createObserver = () => {
    log('Creating observer');
    return new IntersectionObserver(
      (entries) => {
        let hasUpdatedListHeight = false;
        for (const entry of entries) {
          const episode = divToEpisodeMap.get(entry.target);
          if (!episode) {
            log('No episode found for', entry.target); // Should never happen
            continue;
          }
          if (entry.isIntersecting) {
            // Note that sometimes we may overwrite an existing entry in the case
            // of a virtualized list being filtered, since it may re-create an element
            // for the same episode based on the new filter / sort state
            currentEntryMap.set(episode.id, entry);
            if (!hasUpdatedListHeight) {
              listHeight = entry.rootBounds
                ? entry.rootBounds.height
                : innerHeight - topNavOffsetPx; // (this fallback may be unnecessary but just in case)
              hasUpdatedListHeight = true;
            }
          } else if (currentEntryMap.has(episode.id)) {
            currentEntryMap.delete(episode.id);
          }
        }

        if (currentEntryMap.size > 0) {
          // Queue up priority bumping if we haven't already
          if (!cancel) {
            log('Queue new run');
            queue();
          }
        } else if (cancel) {
          log('Cancel scoring (nothing visible)');
          // Cancel priority bumping since nothing is in view
          cancel();
          cancel = undefined;
        }
      },
      {
        threshold: 0.75, // Most of content (title + description) must be visible
        root: getScrollContainer(),
        rootMargin: `-${topNavOffsetPx}px 0px 0px 0px`, // Compensate for fixed top navigation
      }
    );
  };

  // Scroll container may not be available when this script is first run, but should be by the
  // time the observer is created
  let _scrollContainer: HTMLElement | null = null;
  function getScrollContainer() {
    _scrollContainer =
      _scrollContainer ||
      window.pageScrollContainer ||
      document.getElementById('page-scroll-container');
    if (!_scrollContainer) throw new Error('No scroll container found');

    return _scrollContainer;
  }

  // Pause priority bumping if the page is hidden
  function resumeIfVisible() {
    if (document.visibilityState === 'visible' && pausedReason === 'hidden') {
      pausedReason = undefined;
      if (currentEntryMap.size > 0) {
        log('Resumed priority bumping because page is visible');
        queue();
      }
    }
  }

  // When user hasn't scrolled for a few seconds, pause priority bumping
  const onScroll = throttle(function scrollHandler() {
    lastScrolledTimestamp = performance.now();
    if (pausedReason === 'no-recent-scroll') {
      pausedReason = undefined;
      if (currentEntryMap.size > 0) {
        log('Resumed priority bumping because user scrolled');
        queue();
      }
    }
  }, 100);

  return {
    // Lazily setup observer when it's needed
    get observer() {
      if (!_observer) {
        _observer = createObserver();
        addEventListener('visibilitychange', resumeIfVisible);
        getScrollContainer().addEventListener('scroll', onScroll, { passive: true });
        exposeOnWindow({
          __debugPriority: {
            currentEntryMap,
            pausedReason,
            cancel,
            lastScrolledTimestamp,
          },
        });
      }
      return _observer;
    },
    cleanup: () => {
      // Cancel any queued priority bumping
      if (cancel) {
        cancel();
        cancel = undefined;
      }
      // Disconnect observer if it's still connected to any elements
      if (_observer) {
        _observer.disconnect();
        removeEventListener('visibilitychange', resumeIfVisible);
        getScrollContainer().removeEventListener('scroll', onScroll);
      }
    },
  };
}
